import type { Poll, PollV2, GroupSettings } from "@/lib/api/polls";
import type { CompletedMatchContext, MatchEvent, PlayerRecord, Rsvp } from "@/lib/api/rsvp";
import type { HomePhase } from "@/components/v2/home/types";
import type { PublicCycleLinkStatus } from "@/lib/v2/publicCycleLinkStatus";
import { submitPublicPollVotes } from "@/lib/api/publicPoll";
import { submitPublicRsvp } from "@/lib/api/publicRsvp";

export type { PublicCycleLinkStatus } from "@/lib/v2/publicCycleLinkStatus";
export {
  derivePublicLinkStatus,
  isTerminalPublicLinkStatus,
} from "@/lib/v2/publicCycleLinkStatus";

export function formatPublicCycleUrl(token: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/join/public/${token}`;
}

export interface CycleShareState {
  token: string | null;
  url: string | null;
  ready: boolean;
}

export function deriveCycleShareState(
  activePoll: Poll | PollV2 | null,
  upcomingEvent: MatchEvent | null
): CycleShareState {
  const pollToken = activePoll?.poll_public_token ?? null;
  const pollReady = Boolean(activePoll?.poll_password_hash);

  if (pollToken) {
    return {
      token: pollToken,
      url: formatPublicCycleUrl(pollToken),
      ready: pollReady,
    };
  }

  const eventToken = upcomingEvent?.rsvp_public_token ?? null;
  const eventReady = Boolean(upcomingEvent?.rsvp_password_hash);

  if (eventToken) {
    return {
      token: eventToken,
      url: formatPublicCycleUrl(eventToken),
      ready: eventReady,
    };
  }

  return { token: null, url: null, ready: false };
}

export type PublicCycleMode = "poll" | "rsvp" | "unavailable";

export async function resolvePublicCycleMode(token: string): Promise<PublicCycleMode> {
  const { supabase } = await import("@/lib/supabase");

  const { data: poll, error: pollError } = await supabase
    .from("polls")
    .select("id, status")
    .eq("poll_public_token", token)
    .maybeSingle();

  if (pollError) throw pollError;

  if (poll?.status === "open") {
    return "poll";
  }

  const { data: event, error: eventError } = await supabase
    .from("match_events")
    .select("id, status")
    .eq("rsvp_public_token", token)
    .in("status", ["convocatoria", "collecting"])
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (eventError) throw eventError;
  if (event) return "rsvp";

  if (poll?.id) {
    const { data: linkedEvent, error: linkedError } = await supabase
      .from("match_events")
      .select("id, status")
      .eq("poll_id", poll.id)
      .in("status", ["convocatoria", "collecting"])
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (linkedError) throw linkedError;
    if (linkedEvent) return "rsvp";
  }

  return "unavailable";
}

type PublicCycleErrorCode =
  | "MISSING_FIELDS"
  | "CYCLE_NOT_FOUND"
  | "PASSWORD_NOT_SET"
  | "INVALID_PASSWORD"
  | "POLL_CLOSED"
  | "INVALID_PLAYER"
  | "EVENT_CLOSED"
  | "EVENT_FULL"
  | "INTERNAL_ERROR"
  | "PUBLIC_CYCLE_FAILED";

async function callPublicCycleApi(
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  const res = await fetch(`${supabaseUrl}/functions/v1/public-cycle`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify(body),
  });

  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((payload as { error?: PublicCycleErrorCode }).error ?? "PUBLIC_CYCLE_FAILED");
  }
  return payload as Record<string, unknown>;
}

function isPublicCycleUnavailable(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return (
    err.message === "PUBLIC_CYCLE_FAILED" ||
    err.message === "INTERNAL_ERROR" ||
    err.message.includes("Failed to fetch")
  );
}

export function mapPublicCycleError(err: unknown, fallback: string): string {
  if (!(err instanceof Error)) return fallback;
  switch (err.message) {
    case "INVALID_PASSWORD":
      return "Contraseña incorrecta";
    case "PASSWORD_NOT_SET":
      return "El link público aún no está activo";
    case "POLL_CLOSED":
      return "La votación ya cerró";
    case "EVENT_CLOSED":
      return "La convocatoria ya cerró";
    case "EVENT_FULL":
      return "No hay más cupos";
    case "INVALID_PLAYER":
      return "Jugador no válido";
    case "INVALID_SLOT":
      return "Opción de votación no válida";
    case "MISSING_FIELDS":
      return "Faltan datos para completar la acción";
    case "PUBLIC_CYCLE_FAILED":
      return "El servidor público no está disponible. Desplegá la edge function public-cycle.";
    default:
      return err.message || fallback;
  }
}

export interface PublicCyclePeekSnapshot {
  group: { id: string; name: string };
  phase: HomePhase;
  linkStatus: PublicCycleLinkStatus;
  lastCompleted: CompletedMatchContext | null;
  resultOverdue: boolean;
  requiresPassword: boolean;
}

export interface PublicCycleHomeSnapshot {
  group: { id: string; name: string };
  phase: HomePhase;
  linkStatus: PublicCycleLinkStatus;
  resultOverdue: boolean;
  poll: Poll | PollV2 | null;
  pollsV2: boolean;
  event: MatchEvent | null;
  rsvps: Rsvp[];
  players: PlayerRecord[];
  groupSettings: GroupSettings | null;
  lastCompleted: CompletedMatchContext | null;
  myRsvpStatus: string | null;
  votedPlayerIds: number[];
}

export async function peekPublicCycleLink(token: string): Promise<PublicCyclePeekSnapshot> {
  const payload = await callPublicCycleApi({
    action: "peek",
    token,
  });
  return {
    group: payload.group as { id: string; name: string },
    phase: payload.phase as HomePhase,
    linkStatus: (payload.linkStatus as PublicCycleLinkStatus) ?? "active",
    lastCompleted: (payload.lastCompleted as CompletedMatchContext | null) ?? null,
    resultOverdue: Boolean(payload.resultOverdue),
    requiresPassword: Boolean(payload.requiresPassword),
  };
}

export async function verifyPublicCyclePassword(
  token: string,
  password: string
): Promise<PublicCycleHomeSnapshot> {
  const payload = await callPublicCycleApi({
    action: "verify",
    token,
    password,
  });
  return mapPublicCycleHome(payload);
}

export async function fetchPublicCycleHome(input: {
  token: string;
  password: string;
  playerId: number;
}): Promise<PublicCycleHomeSnapshot> {
  const payload = await callPublicCycleApi({
    action: "fetch_home",
    token: input.token,
    password: input.password,
    playerId: input.playerId,
  });
  return mapPublicCycleHome(payload);
}

export async function joinPublicCycleGuest(input: {
  token: string;
  password: string;
  guestName: string;
}): Promise<number> {
  const payload = await callPublicCycleApi({
    action: "join_guest",
    token: input.token,
    password: input.password,
    guestName: input.guestName,
  });
  return payload.playerId as number;
}

export async function submitPublicCyclePollVotes(input: {
  token: string;
  password: string;
  playerId: number;
  slotIds: string[];
}): Promise<Poll | PollV2 | null> {
  try {
    const payload = await callPublicCycleApi({
      action: "vote_poll",
      token: input.token,
      password: input.password,
      playerId: input.playerId,
      slotIds: input.slotIds,
    });
    return (payload.poll as Poll | PollV2) ?? null;
  } catch (err) {
    if (!isPublicCycleUnavailable(err)) throw err;
    await submitPublicPollVotes({
      token: input.token,
      password: input.password,
      slotIds: input.slotIds,
      playerId: input.playerId,
    });
    return null;
  }
}

export async function submitPublicCycleRsvp(input: {
  token: string;
  password: string;
  playerId: number;
  status?: "going" | "maybe" | "out";
  clear?: boolean;
}): Promise<string | null> {
  try {
    const payload = await callPublicCycleApi({
      action: "submit_rsvp",
      token: input.token,
      password: input.password,
      playerId: input.playerId,
      status: input.status,
      clear: input.clear,
    });
    if (input.clear) return null;
    return (payload.status as string) ?? input.status ?? null;
  } catch (err) {
    if (!isPublicCycleUnavailable(err)) throw err;
    if (input.clear) {
      throw err;
    }
    await submitPublicRsvp({
      token: input.token,
      password: input.password,
      status: input.status!,
      playerId: input.playerId,
    });
    return input.status ?? null;
  }
}

function mapPublicCycleHome(payload: Record<string, unknown>): PublicCycleHomeSnapshot {
  return {
    group: payload.group as { id: string; name: string },
    phase: payload.phase as HomePhase,
    linkStatus: (payload.linkStatus as PublicCycleLinkStatus) ?? "active",
    resultOverdue: Boolean(payload.resultOverdue),
    poll: (payload.poll as Poll | PollV2 | null) ?? null,
    pollsV2: Boolean(payload.pollsV2),
    event: (payload.event as MatchEvent | null) ?? null,
    rsvps: (payload.rsvps as Rsvp[]) ?? [],
    players: (payload.players as PlayerRecord[]) ?? [],
    groupSettings: (payload.groupSettings as GroupSettings | null) ?? null,
    lastCompleted: (payload.lastCompleted as CompletedMatchContext | null) ?? null,
    myRsvpStatus: (payload.myRsvpStatus as string | null) ?? null,
    votedPlayerIds: (payload.votedPlayerIds as number[]) ?? [],
  };
}
